/**
 * Pure data mapping for the TradingView Lightweight Charts candlestick + volume chart.
 *
 * No React, no DOM, no network: it only converts TekInvest's EXISTING API payloads
 * (`GET /stocks/scanner/{symbol}/history` -> `StockHistoryResponse`) into the shapes the
 * chart library expects, and defines the extension types for later phases (indicator line
 * overlays, algo trade markers) so the chart component never has to change shape.
 *
 * Lightweight Charts throws on unsorted or duplicate `time` values, and Decimal fields
 * arrive from the API as strings, so every conversion here is defensive and counts what it
 * drops instead of failing the whole chart.
 */
import type { StockHistoryIndicatorPoint, StockHistoryPricePoint } from "@/types";

/**
 * Daily bars use the business-day string form "YYYY-MM-DD" (the API's `trade_date`).
 * Intraday charts (admin trade review) use UTC epoch SECONDS (a number); see `zonedTime.ts`.
 */
export type ChartTime = string | number;

export interface CandlePoint {
  time: ChartTime;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface VolumePoint {
  time: ChartTime;
  value: number;
  color: string;
}

export interface CandlestickChartData {
  candles: CandlePoint[];
  volume: VolumePoint[];
  /** Rows dropped as unusable (bad date, non-numeric / non-positive price, high < low). */
  skippedRows: number;
}

// ---- Extension seams for later phases (supported by the chart component, unused in Phase 1) ----

export interface ChartLinePoint {
  time: ChartTime;
  value: number;
}

/** A line drawn on the price pane, e.g. an EMA. */
export interface ChartLineOverlay {
  id: string;
  label: string;
  color: string;
  data: ChartLinePoint[];
}

/** A marker on the candle series, e.g. an algo entry/exit. Positioned by TIME only. */
export interface ChartMarker {
  time: ChartTime;
  position: "aboveBar" | "belowBar" | "inBar";
  shape: "arrowUp" | "arrowDown" | "circle" | "square";
  color: string;
  text?: string;
  id?: string;
}

export const CANDLE_UP_COLOR = "#2E7D32";
export const CANDLE_DOWN_COLOR = "#C62828";
// Volume bars use the candle colours at reduced opacity so they sit behind the price action.
export const VOLUME_UP_COLOR = "rgba(46, 125, 50, 0.45)";
export const VOLUME_DOWN_COLOR = "rgba(198, 40, 40, 0.45)";

const ISO_DATE = /^(\d{4}-\d{2}-\d{2})/;

function toIsoDate(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = ISO_DATE.exec(value);
  return match ? match[1] : null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Converts daily price rows into ascending, de-duplicated candle and volume series.
 * When the same date appears twice the LAST row wins (a later import supersedes an
 * earlier one). Volume is clamped to >= 0; a missing volume becomes 0 (indices have none).
 */
export function toCandlestickChartData(prices: readonly StockHistoryPricePoint[] | null | undefined): CandlestickChartData {
  const byDate = new Map<string, { candle: CandlePoint; volume: number }>();
  let skippedRows = 0;

  for (const row of prices ?? []) {
    const time = toIsoDate(row?.trade_date);
    const open = toNumber(row?.open);
    const high = toNumber(row?.high);
    const low = toNumber(row?.low);
    const close = toNumber(row?.close);
    if (
      time === null ||
      open === null ||
      high === null ||
      low === null ||
      close === null ||
      open <= 0 ||
      high <= 0 ||
      low <= 0 ||
      close <= 0 ||
      high < low
    ) {
      skippedRows += 1;
      continue;
    }
    const rawVolume = toNumber(row.volume);
    byDate.set(time, {
      candle: { time, open, high, low, close },
      volume: rawVolume !== null && rawVolume > 0 ? rawVolume : 0,
    });
  }

  // ISO dates sort correctly as strings.
  const times = Array.from(byDate.keys()).sort();
  const candles: CandlePoint[] = [];
  const volume: VolumePoint[] = [];
  for (const time of times) {
    const entry = byDate.get(time)!;
    candles.push(entry.candle);
    volume.push({
      time,
      value: entry.volume,
      color: entry.candle.close >= entry.candle.open ? VOLUME_UP_COLOR : VOLUME_DOWN_COLOR,
    });
  }
  return { candles, volume, skippedRows };
}

type IndicatorField = Exclude<keyof StockHistoryIndicatorPoint, "trade_date">;

/**
 * Later-phase helper: turns one indicator column (e.g. `ema_20`) of the SAME history
 * response into a line overlay. Null / non-numeric warm-up values are dropped (the
 * library rejects them); the result is ascending and de-duplicated.
 */
export function indicatorToLineOverlay(
  indicators: readonly StockHistoryIndicatorPoint[] | null | undefined,
  field: IndicatorField,
  meta: { id: string; label: string; color: string }
): ChartLineOverlay {
  const byDate = new Map<string, number>();
  for (const row of indicators ?? []) {
    const time = toIsoDate(row?.trade_date);
    const value = toNumber(row?.[field]);
    if (time !== null && value !== null) byDate.set(time, value);
  }
  const data = Array.from(byDate.keys())
    .sort()
    .map((time) => ({ time, value: byDate.get(time)! }));
  return { ...meta, data };
}

/** Last candle date, for the "as of" caption; null when there is no data. */
export function lastCandleDate(candles: readonly CandlePoint[]): string | null {
  const last = candles.length > 0 ? candles[candles.length - 1].time : null;
  return typeof last === "string" ? last : null;
}

// ---- EMA overlays (Phase 2a) ----

export type EmaId = "ema20" | "ema50" | "ema200";

/** The EMA columns the history API already returns, with their chart colours. */
export const EMA_OVERLAY_DEFS: ReadonlyArray<{
  id: EmaId;
  field: "ema_20" | "ema_50" | "ema_200";
  label: string;
  color: string;
}> = [
  { id: "ema20", field: "ema_20", label: "EMA 20", color: "#F9A825" },
  { id: "ema50", field: "ema_50", label: "EMA 50", color: "#1565C0" },
  { id: "ema200", field: "ema_200", label: "EMA 200", color: "#6A1B9A" },
];

// ---- Data freshness (Phase 2a) ----

/**
 * The browser has no exchange-holiday calendar, so a single missing weekday (a holiday or a
 * not-yet-imported session) is tolerated; two or more missing weekdays is flagged as stale.
 */
export const STALE_AFTER_MISSED_WEEKDAYS = 2;

export interface DataFreshness {
  status: "fresh" | "stale" | "unknown";
  lastBarDate: string | null;
  /** Today's date in IST (the exchange's calendar), YYYY-MM-DD. */
  today: string;
  /** Mon-Fri days strictly between the last bar and today. */
  missedWeekdays: number;
}

/** Today's calendar date in IST as YYYY-MM-DD, independent of the viewer's timezone. */
export function istToday(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDay(iso: string): number | null {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  const [y, mo, d] = m[1].split("-").map(Number);
  const t = Date.UTC(y, mo - 1, d);
  return Number.isFinite(t) ? t : null;
}

/** Weekdays (Mon-Fri) strictly after `lastBarDate` and strictly before `today`. */
export function countMissedWeekdays(lastBarDate: string, today: string): number | null {
  const start = utcDay(lastBarDate);
  const end = utcDay(today);
  if (start === null || end === null) return null;
  let count = 0;
  for (let t = start + DAY_MS; t < end; t += DAY_MS) {
    const dow = new Date(t).getUTCDay();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
}

/** Classifies the newest daily bar against today's IST date. Pure; `now` is injectable. */
export function assessFreshness(lastBarDate: string | null, now: Date = new Date()): DataFreshness {
  const today = istToday(now);
  if (lastBarDate === null) return { status: "unknown", lastBarDate, today, missedWeekdays: 0 };
  const missed = countMissedWeekdays(lastBarDate, today);
  if (missed === null) return { status: "unknown", lastBarDate, today, missedWeekdays: 0 };
  return {
    status: missed >= STALE_AFTER_MISSED_WEEKDAYS ? "stale" : "fresh",
    lastBarDate,
    today,
    missedWeekdays: missed,
  };
}
